/**
 * What fills the ad wall's atlas: the posters, then the videos.
 *
 * Owns the texture's contents from the moment it is allocated. Each poster is
 * uploaded into its slot the moment it lands, so a slow request hides one
 * card and not the wall; each video, once it is wanted, decodes off-screen
 * and has its new frames copied into the same slot. `AdRevealBackground`
 * owns the context, the loop and the uniforms, and asks this for two things:
 * `uploadFrames` once per animation frame, and `sync` whenever something
 * that decides whether video should run has changed.
 *
 * VIDEO IS DESK LAYOUTS ONLY, and only while motion is allowed. A phone shows
 * two columns, mostly behind the form, and ten decoders are more than a
 * handset's media stack runs at once: iOS caps simultaneous playback and
 * quietly stalls the rest, and the battery pays for the ones that do run.
 * Reduced motion freezes the wall, and a reader on data saver did not ask
 * for seven megabytes behind a form. In each case the poster is the card.
 * These are checked on every `sync`, not once: a preference flipped mid-
 * session pauses the videos where they are, and a window widened past the
 * phone step starts them.
 */

import {
  CREATIVES,
  creativeSrc,
  drawCard,
  loadPoster,
  SLOT_H,
  SLOT_W,
  slotOrigin,
  TEX_H,
  TEX_W,
} from "./adWallAtlas";
import { buildChrome } from "./adWallChrome";

/**
 * One creative's video, decoding off-screen into its atlas slot.
 *
 * `live` flips when the first frame has decoded, which is also when the
 * element knows its own size; before that the slot keeps its poster. `dirty`
 * is set by the browser's frame callback so a frame is copied once, when it
 * is new, rather than on every animation tick. A browser without that
 * callback (`hasFrameCallback` false) copies every tick instead, which is
 * wasteful but correct.
 */
type Player = {
  video: HTMLVideoElement;
  index: number;
  live: boolean;
  dirty: boolean;
  hasFrameCallback: boolean;
};

/** The frame callback is in every current engine, but not yet in the typings. */
type VideoWithFrameCallback = HTMLVideoElement & {
  requestVideoFrameCallback?: (callback: () => void) => number;
};

/** Browsers report data saver here; the typings do not know the field. */
type NavigatorWithConnection = Navigator & {
  connection?: { saveData?: boolean };
};

export type WallConditions = {
  /** The host is phone-width (see `MOBILE_MAX_CSS_PX` in the component). */
  narrow: boolean;
  /** `prefers-reduced-motion: reduce` is on. */
  reduced: boolean;
  /** The document is hidden; nothing drawn would be seen. */
  hidden: boolean;
};

export type WallPlayer = {
  /** Copy every new video frame into the atlas. Once per animation frame. */
  uploadFrames(): void;
  /** Start, resume or pause the videos to match the conditions. */
  sync(conditions: WallConditions): void;
  /** Stop everything and release the decoders. */
  dispose(): void;
};

/**
 * `family` is the resolved sans stack and `fontReady` resolves once it can be
 * measured; the chrome is built after that, so a fallback face measured too
 * early does not end up stamped on every frame.
 */
export function createWallPlayer(
  gl: WebGLRenderingContext,
  tex: WebGLTexture | null,
  family: string,
  fontReady: Promise<void>,
): WallPlayer {
  let disposed = false;
  let players: Player[] = [];
  let wanted = false;
  const chromes: (HTMLCanvasElement | null)[] = CREATIVES.map(() => null);

  // One slot-sized 2D canvas that every poster and frame is drawn through on
  // its way to the texture. `texSubImage2D` copies a source at its own pixel
  // size, and the sources come in several sizes, so the fit and the card clip
  // happen here, with the same `drawCard` for both.
  const scratch = document.createElement("canvas");
  scratch.width = SLOT_W;
  scratch.height = SLOT_H;
  const scratchCtx = scratch.getContext("2d");

  const bind = () => {
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, tex);
  };

  // The whole atlas, transparent, so every slot can be patched in on its own
  // schedule. Transparent samples resolve to the page background in the
  // shader, so an empty slot is simply not there yet.
  bind();
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
  gl.texImage2D(
    gl.TEXTURE_2D,
    0,
    gl.RGBA,
    TEX_W,
    TEX_H,
    0,
    gl.RGBA,
    gl.UNSIGNED_BYTE,
    null,
  );

  const uploadSlot = (
    index: number,
    source: HTMLImageElement | HTMLVideoElement | null,
  ) => {
    if (disposed || !scratchCtx) return;
    const { x, y } = slotOrigin(index);
    drawCard(scratchCtx, source, 0, 0, chromes[index]);
    bind();
    gl.texSubImage2D(
      gl.TEXTURE_2D,
      0,
      x,
      y,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      scratch,
    );
  };

  // The chrome waits on the face alone, so a stalled poster cannot leave a
  // playing video without its handle, rail and button. A frame copied
  // before the face is ready goes up bare and the next one carries it.
  const chromeReady = fontReady.then(() => {
    if (disposed) return;
    CREATIVES.forEach((creative, index) => {
      chromes[index] = buildChrome(index, creative.brand, family);
    });
  });

  // Each poster on its own: the first to land is on the wall while the rest
  // are still in flight, and one that never lands leaves one plain card. It
  // waits on the chrome because it is stamped with it once, not every frame.
  CREATIVES.forEach((creative, index) => {
    Promise.all([loadPoster(creative), chromeReady]).then(([poster]) => {
      if (disposed) return;
      uploadSlot(index, poster);
    });
  });

  const start = () => {
    players = CREATIVES.map((creative, index) => {
      const video: VideoWithFrameCallback = document.createElement("video");
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.preload = "auto";
      const player: Player = {
        video,
        index,
        live: false,
        dirty: false,
        hasFrameCallback: typeof video.requestVideoFrameCallback === "function",
      };
      if (video.requestVideoFrameCallback) {
        const onFrame = () => {
          if (disposed) return;
          player.live = true;
          player.dirty = true;
          video.requestVideoFrameCallback?.(onFrame);
        };
        video.requestVideoFrameCallback(onFrame);
      } else {
        video.addEventListener("playing", () => {
          player.live = true;
        });
      }
      video.src = creativeSrc(creative, "mp4");
      return player;
    });
  };

  return {
    uploadFrames() {
      if (disposed || !players.length) return;
      for (const player of players) {
        if (!player.live) continue;
        if (player.hasFrameCallback && !player.dirty) continue;
        const { video } = player;
        if (video.paused || video.readyState < video.HAVE_CURRENT_DATA)
          continue;
        player.dirty = false;
        uploadSlot(player.index, video);
      }
    },

    sync({ narrow, reduced, hidden }) {
      if (disposed) return;
      const saveData =
        (navigator as NavigatorWithConnection).connection?.saveData === true;
      wanted = !narrow && !reduced && !saveData;
      // Created on the first want, never before: a phone that is never widened
      // never fetches a byte of video.
      if (wanted && !players.length) start();
      for (const { video } of players) {
        // `play()` can be declined (a policy, a background tab); the catch is
        // the poster, or the last frame, staying put. Decoding ten videos into
        // a tab nobody is looking at is the one cost here worth stopping.
        if (wanted && !hidden) void video.play().catch(() => {});
        else video.pause();
      }
    },

    dispose() {
      disposed = true;
      // Dropping the source and reloading is what actually releases a
      // decoder; a paused element that still points at a file keeps it.
      for (const { video } of players) {
        video.pause();
        video.removeAttribute("src");
        video.load();
      }
      players = [];
    },
  };
}
